'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Calendar, Clock, Target, Lightbulb, Star, Eye, Brain, Heart, ArrowRight, TrendingUp, BarChart } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function ZamanCizelgesiPage() {
  const timelineVariants = [
    {
      type: "Haftalık Yayılım",
      cards: 7,
      description: "Haftanın her günü için bir kart",
      usage: "Haftalık planlama, kısa vadeli hedefler",
      layout: "Yatay sıra - Pazartesi'den Pazar'a"
    },
    {
      type: "Aylık Yayılım",
      cards: 12,
      description: "Yılın her ayı için bir kart",
      usage: "Yıllık planlama, sezonsal değişimler",
      layout: "3x4 grid - Ocak'tan Aralık'a"
    },
    {
      type: "Çeyrek Yıl Yayılımı",
      cards: 4,
      description: "Her mevsim için bir kart",
      usage: "Mevsimsel enerji takibi",
      layout: "2x2 grid - mevsim sırası"
    },
    {
      type: "Yaşam Dönemleri",
      cards: 9,
      description: "Geçmiş, şimdi, gelecek dönemleri",
      usage: "Uzun vadeli yaşam analizi",
      layout: "3x3 grid - zaman matrisi"
    }
  ];

  const monthlyLayout = [
    { month: 1, name: "Ocak", season: "Kış", energy: "Yeni Başlangıçlar" },
    { month: 2, name: "Şubat", season: "Kış", energy: "Temelleri Güçlendirme" },
    { month: 3, name: "Mart", season: "İlkbahar", energy: "Büyüme ve Yenilenme" },
    { month: 4, name: "Nisan", season: "İlkbahar", energy: "Hızlı Gelişim" },
    { month: 5, name: "Mayıs", season: "İlkbahar", energy: "Çiçeklenme" },
    { month: 6, name: "Haziran", season: "Yaz", energy: "Doruk Noktası" },
    { month: 7, name: "Temmuz", season: "Yaz", energy: "Güçlü Enerji" },
    { month: 8, name: "Ağustos", season: "Yaz", energy: "Hasat Zamanı" },
    { month: 9, name: "Eylül", season: "Sonbahar", energy: "Denge ve Değerlendirme" },
    { month: 10, name: "Ekim", season: "Sonbahar", energy: "İç Dünyaya Dönüş" },
    { month: 11, name: "Kasım", season: "Sonbahar", energy: "Dönüşüm" },
    { month: 12, name: "Aralık", season: "Kış", energy: "Kapanış ve Hazırlık" }
  ];

  const interpretationTechniques = [
    {
      technique: "Döngüsel Okuma",
      description: "Kartları döngü olarak değerlendirin - bir dönem diğerine nasıl bağlanıyor?",
      icon: <Calendar className="h-5 w-5" />
    },
    {
      technique: "Enerji Akışı",
      description: "Kartlardaki enerji artış mı azalış mı gösteriyor? Hangi dönemlerde zirve?",
      icon: <TrendingUp className="h-5 w-5" />
    },
    {
      technique: "Tema Analizi",
      description: "Belirli dönemlerde aynı tema/element tekrar ediyor mu?",
      icon: <BarChart className="h-5 w-5" />
    },
    {
      technique: "Geçiş Noktaları",
      description: "Bir dönemden diğerine geçişte hangi değişimler oluyor?",
      icon: <Brain className="h-5 w-5" />
    }
  ];

  const timingTips = [
    {
      period: "Haftalık",
      bestTime: "Pazar akşamı",
      purpose: "Haftayı planlama",
      notes: "Kısa vadeli hedefler için ideal"
    },
    {
      period: "Aylık",
      bestTime: "Ayın son haftası",
      purpose: "Ay değerlendirmesi",
      notes: "Orta vadeli projeler için"
    },
    {
      period: "Yıllık",
      bestTime: "Yılbaşı veya doğum günü",
      purpose: "Büyük resim görme",
      notes: "Uzun vadeli yaşam planlaması"
    },
    {
      period: "Mevsimsel",
      bestTime: "Equinox/Solstice",
      purpose: "Doğal döngülerle uyum",
      notes: "Enerji değişimlerini takip"
    }
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Okuma Teknikleri</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Zaman Çizelgesi</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/interpretation-techniques"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Okuma Teknikleri
          </Link>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-xl p-8 mb-8 border border-purple-500/20">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-100">Zaman Çizelgesi Yayılımı</h1>
              <p className="text-purple-300 font-medium">İleri Seviye • 4-12 Kart</p>
            </div>
          </div>
          <p className="text-lg text-slate-200 leading-relaxed">
            Haftalık, aylık veya yıllık projeksiyonlar sunar. Zamanın akışını takip ederek
            gelecek planlaması yapar. Döngüsel enerji değişimlerini anlama ve zamanlama için ideal.
          </p>
        </div>

        {/* Ana Bilgiler */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="h-6 w-6 text-purple-400" />
              <h3 className="text-lg font-semibold text-slate-100">Süre</h3>
            </div>
            <p className="text-slate-300">30-60 dakika</p>
            <p className="text-slate-400 text-sm">Kart sayısına göre değişir</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Target className="h-6 w-6 text-purple-400" />
              <h3 className="text-lg font-semibold text-slate-100">Amaç</h3>
            </div>
            <p className="text-slate-300">Zaman planlaması</p>
            <p className="text-slate-400 text-sm">Gelecek projeksiyonu</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Lightbulb className="h-6 w-6 text-purple-400" />
              <h3 className="text-lg font-semibold text-slate-100">Seviye</h3>
            </div>
            <p className="text-slate-300">İleri</p>
            <p className="text-slate-400 text-sm">Zaman yönetimi bilgisi</p>
          </div>
        </div>

        {/* Zaman Çizelgesi Varyantları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Zaman Çizelgesi Varyantları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {timelineVariants.map((variant, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{variant.cards}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-100">{variant.type}</h3>
                </div>
                <p className="text-slate-300 text-sm mb-2">{variant.description}</p>
                <p className="text-slate-400 text-xs mb-2 italic">{variant.usage}</p>
                <div className="bg-slate-700 rounded-lg p-2">
                  <p className="text-slate-300 text-xs"><strong>Dizilim:</strong> {variant.layout}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Aylık Yayılım Örneği */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Aylık Yayılım Örneği (12 Kart)</h2>
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-6">
              {monthlyLayout.map((month) => (
                <div key={month.month} className={`rounded-lg p-3 text-center text-white font-bold text-xs ${
                  month.season === 'İlkbahar' ? 'bg-green-600' :
                  month.season === 'Yaz' ? 'bg-yellow-600' :
                  month.season === 'Sonbahar' ? 'bg-orange-600' :
                  'bg-blue-600'
                }`}>
                  {month.month}<br/>
                  <span className="text-xs font-normal">{month.name}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-slate-100 font-semibold mb-2">Mevsimsel Enerji Dağılımı</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                    <span className="text-slate-300">Kış (12,1,2): İç dünya, planlama</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                    <span className="text-slate-300">İlkbahar (3,4,5): Büyüme, yenilik</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-yellow-600 rounded-full"></div>
                    <span className="text-slate-300">Yaz (6,7,8): Doruk, başarı</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-orange-600 rounded-full"></div>
                    <span className="text-slate-300">Sonbahar (9,10,11): Hasat, değerlendirme</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-slate-100 font-semibold mb-2">Okuma İpuçları</h4>
                <div className="space-y-1 text-sm text-slate-300">
                  <p>• Aynı element/sayı tekrarlarını arayın</p>
                  <p>• Mevsimsel geçişlerdeki değişimi not edin</p>
                  <p>• Yılın hangi dönemi daha yoğun?</p>
                  <p>• Zorluklu ayları destekleyici aylarla dengeleyin</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Yorumlama Teknikleri */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yorumlama Teknikleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {interpretationTechniques.map((tech, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                    {tech.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-100">{tech.technique}</h3>
                </div>
                <p className="text-slate-300">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Zamanlama İpuçları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">En İyi Zamanlama</h2>
          <div className="space-y-4">
            {timingTips.map((tip, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100">{tip.period}</h3>
                    <p className="text-purple-300 text-sm">{tip.bestTime}</p>
                  </div>
                  <div>
                    <p className="text-slate-300 text-sm font-medium">{tip.purpose}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-slate-400 text-sm italic">{tip.notes}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Okuma Süreci */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Adım Adım Süreç</h2>
          <div className="space-y-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Zaman Dilimini Seçin</h3>
                  <p className="text-slate-300 mb-2">
                    Hangi zaman dilimini incelemek istediğinize karar verin: haftalık, aylık, 
                    yıllık veya özel bir dönem. Her birinin farklı avantajları vardır.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Kartları Kronolojik Sırayla Dizin</h3>
                  <p className="text-slate-300 mb-2">
                    Kartları zaman sırasına göre yerleştirin. İlk kart ilk dönem, 
                    son kart son dönemi temsil eder. Her kartın hangi zamanı temsil ettiğini not edin.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Enerji Akışını Analiz Edin</h3>
                  <p className="text-slate-300 mb-2">
                    Kartların genel enerjisini takip edin. Hangi dönemlerde yüksek, 
                    hangi dönemlerde düşük enerji var? Döngüsel bir desen var mı?
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Aksiyon Planı Oluşturun</h3>
                  <p className="text-slate-300">
                    Her dönem için somut eylemler belirleyin. Zorluklu dönemleri nasıl 
                    destekleyebilirsiniz? Güçlü dönemleri nasıl en iyi şekilde değerlendirebilirsiniz?
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dikkat Edilmesi Gerekenler */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Önemli Notlar</h2>
          <div className="space-y-4">
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">🔮 Kehanet Değil, Olasılık</h3>
              <p className="text-amber-100">
                Zaman çizelgesi kesin olayları göstermez, o dönemki enerji ve eğilimleri gösterir.
                Kendi seçimlerinizle bu enerjileri yönlendirebilirsiniz.
              </p>
            </div>
            
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">📅 Esnek Kalın</h3>
              <p className="text-amber-100">
                Zamanlamalar değişebilir. Kartlar genel eğilimleri gösterir, 
                kesin tarihleri değil. Dönemsel enerjiye odaklanın.
              </p>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">📊 Döngüleri Takip Edin</h3>
              <p className="text-amber-100">
                Aynı desenleri farklı zaman dilimlerinde gözlemleyebilirsiniz.
                Bu size kişisel döngülerinizi anlama imkanı verir.
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/interpretation-techniques/astrolojik-yayilim"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Astrolojik Yayılım
          </Link>

          <Link
            href="/about/tarot/interpretation-techniques/chakra-yayilimi"
            className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Chakra Yayılımı
            <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}